import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Table, Section } from "barte-design-system";
import type { ColumnDefinition } from "barte-design-system";
import { PixLogoIcon, BarcodeIcon, CreditCardIcon } from "@phosphor-icons/react";

import { Skeleton } from "@shared/components";
import { formatDelay, formatPercentage, formatCurrency } from "@shared/helpers";
import type { ISellerRates, ISellerRatesPixConfig, ISellerRatesBankSlipConfig, ISellerRatesSightCreditConfig, ISellerRatesDebitConfig } from "@payfac/types";

import { useGetBarteTaxes } from "@payfac/services";
import { extractAvailableDelays } from "@payfac/helpers";
import { ModalEditFlatRate } from "../modal-edit-flat-rate";
import type { FlatRateFormData } from "../modal-edit-flat-rate/modal-edit-flat-rate.type";
import { ModalEditCreditSimple } from "../modal-edit-credit-simple";
import { ModalImportRates } from "../modal-import-rates";

import type { CreditInstallmentRow, RatesDetailsProps } from "./rates-details.type";
import styles from "./rates-details.module.css";

export const RatesDetails = ({
    data,
    isLoading,
    onSubmitData,
    onImportData,
    isPending,
    sellerId,
}: RatesDetailsProps) => {
    const { t } = useTranslation();

    const [openModal, setOpenModal] = useState<string>("");
    const [receiptType, setReceiptType] = useState<string>("early");

    const { data: barteTaxes } = useGetBarteTaxes();
    const availableDelays = barteTaxes ? extractAvailableDelays(barteTaxes) : [];

    const seller = data?.currentSellerRatesConfig;

    useEffect(() => {
        if (data?.receiptType) setReceiptType(data.receiptType);
    }, [data?.receiptType]);
    const buyer = data?.defaultRatesConfig;

    const handleFlatSubmit = (
        configKey: keyof ISellerRates["currentSellerRatesConfig"],
        formData: FlatRateFormData,
    ) => {
        const updated: ISellerRates = {
            ...data,
            currentSellerRatesConfig: {
                ...seller,
                [configKey]: { ...(seller?.[configKey] as ISellerRatesPixConfig | ISellerRatesBankSlipConfig | ISellerRatesSightCreditConfig | ISellerRatesDebitConfig), ...formData },
            },
        } as ISellerRates;

        onSubmitData(sellerId, updated, () => setOpenModal(""));
    };

    const handleCreditSubmit = (
        newSellerRates: number[],
        newBuyerRates: number[],
        newDelay: number | null,
        newReceiptType: string,
        newSightDelay: number | null,
        newSightRate: number | null,
    ) => {
        const updated: ISellerRates = {
            ...data,
            receiptType: newReceiptType,
            currentSellerRatesConfig: {
                ...seller,
                sightCreditCardConfigData: {
                    ...seller?.sightCreditCardConfigData,
                    delayForRedeem: newSightDelay ?? seller?.sightCreditCardConfigData?.delayForRedeem ?? 0,
                    rate: newSightRate ?? seller?.sightCreditCardConfigData?.rate ?? 0,
                },
                earlyCreditCardConfigData: {
                    ...seller?.earlyCreditCardConfigData,
                    sellerRates: newSellerRates,
                    buyerRates: newBuyerRates,
                    delayForRedeem: newDelay ?? seller?.earlyCreditCardConfigData?.delayForRedeem ?? 0,
                },
            },
        } as ISellerRates;

        onSubmitData(sellerId, updated, () => {
            setReceiptType(newReceiptType);
            setOpenModal("");
        });
    };

    const handleImportSubmit = (payload: Record<string, any>, onSuccess: () => void) => {
        onImportData(payload, onSuccess);
    };

    const buildFlatRateData = (
        configKey: keyof ISellerRates["currentSellerRatesConfig"],
    ): FlatRateFormData => {
        const config = seller?.[configKey] as
            | ISellerRatesPixConfig
            | ISellerRatesBankSlipConfig
            | ISellerRatesSightCreditConfig
            | ISellerRatesDebitConfig;

        return {
            delayForRedeem: config?.delayForRedeem ?? 0,
            rate: (config as ISellerRatesPixConfig)?.rate ?? 0,
            rateLimitValue: (config as ISellerRatesPixConfig)?.rateLimitValue,
            rateType: (config as ISellerRatesBankSlipConfig)?.rateType,
        };
    };

    const skeletonContent = <Skeleton height="1rem" width="60%" />;

    const val = (content: string) => isLoading ? skeletonContent : content;

    const editButton = (modalKey: string) => (
        <Button size="sm" variant="outline" disabled={isLoading} onClick={() => setOpenModal(modalKey)}>
            {t("payfac.pageRatePlanDetailsEditButton")}
        </Button>
    );

    const creditColumns: ColumnDefinition<CreditInstallmentRow>[] = [
        {
            key: "installment",
            title: t("payfac.pageRatesTableColumnInstallment"),
            dataIndex: "installment",
            width: 80,
            ...(isLoading ? { render: () => skeletonContent } : {}),
        },
        {
            key: "buyerRate",
            title: t("payfac.pageRatesTableColumnBuyerRate"),
            dataIndex: "buyerRate",
            ...(isLoading ? { render: () => skeletonContent } : {}),
        },
        {
            key: "sellerRate",
            title: t("payfac.pageRatesTableColumnSellerRate"),
            dataIndex: "sellerRate",
            ...(isLoading ? { render: () => skeletonContent } : {}),
        },
    ];

    const buildCreditRows = (): CreditInstallmentRow[] => {
        if (isLoading) {
            return Array.from({ length: 12 }).map((_, i) => ({
                key: i,
                installment: "",
                buyerRate: "",
                sellerRate: "",
                mostUsed: false,
            }));
        }

        const sellerRates = seller?.earlyCreditCardConfigData?.sellerRates || [];
        const buyerRates = seller?.earlyCreditCardConfigData?.buyerRates || [];
        const mostUsed = seller?.earlyCreditCardConfigData?.mostUsed || [];

        return sellerRates.map((rate, i) => ({
            key: i,
            installment: `${i + 2}x`,
            buyerRate: formatPercentage(buyerRates[i]),
            sellerRate: formatPercentage(rate),
            mostUsed: mostUsed.includes(i + 2),
        }));
    };

    return (
        <div className={styles.content}>
            {/*codigo utilizado em ambiente de testes para facilitar testes com taxas importando pelo JSON*/}
            {/* <div>
                <Button variant="outline" size="sm" onClick={() => setOpenModal("import")}>
                    {t("payfac.pageRatesImportButton")}
                </Button>
            </div> */}

            <Section
                title={t("payfac.pageRatePlanDetailsPix")}
                headerActions={editButton("pix")}
                variant="two-columns"
                secondColumn={
                    <>
                        <Section.Row label={t("payfac.pageRatesTableColumnLimit")} content={val(formatCurrency(seller?.pixConfigData?.rateLimitValue))} />
                    </>
                }
            >
                <Section.Row label={t("payfac.pageRatePlanDetailsRedeemTime")} content={val(formatDelay(seller?.pixConfigData?.delayForRedeem))} />

                <Section.Row label={t("payfac.pageRatesTableColumnSellerRate")} content={val(formatPercentage(seller?.pixConfigData?.rate))} />
            </Section>

            <Section
                title={t("payfac.pageRatePlanDetailsBoleto")}
                headerActions={editButton("bankSlip")}
                variant="two-columns"
                secondColumn={
                    <>
                        <Section.Row
                            label={t("payfac.pageRatesTableColumnSellerRate")}
                            content={val(
                                seller?.bankSlipConfigData?.rateType === "value"
                                    ? formatCurrency(seller?.bankSlipConfigData?.rate)
                                    : formatPercentage(seller?.bankSlipConfigData?.rate)
                            )}
                        />
                        <Section.Row label={t("payfac.pageRatesTableColumnLimit")} content={val(formatCurrency(seller?.bankSlipConfigData?.rateLimitValue))} />
                    </>
                }
            >
                <Section.Row label={t("payfac.pageRatePlanDetailsRedeemTime")} content={val(formatDelay(seller?.bankSlipConfigData?.delayForRedeem))} />
                <Section.Row
                    label={t("payfac.pageRatesBankSlipRateTypeLabel")}
                    content={val(
                        seller?.bankSlipConfigData?.rateType === "percent"
                            ? t("payfac.pageRatesBankSlipRateTypePercent")
                            : seller?.bankSlipConfigData?.rateType === "value"
                                ? t("payfac.pageRatesBankSlipRateTypeValue")
                                : seller?.bankSlipConfigData?.rateType ?? "-"
                    )}
                />
            </Section>

            <Section
                title={t("payfac.pageRatePlanDetailsDebitCard")}
                headerActions={editButton("debit")}
                variant="two-columns"
                secondColumn={
                    <Section.Row label={t("payfac.pageRatesTableColumnSellerRate")} content={val(formatPercentage(seller?.debitCardConfigData?.rate))} />
                }
            >
                <Section.Row label={t("payfac.pageRatePlanDetailsRedeemTime")} content={val(formatDelay(seller?.debitCardConfigData?.delayForRedeem))} />
            </Section>

            <Section
                title={t("payfac.pageRatesCreditTitle")}
                headerActions={editButton("credit")}
                variant="two-columns"
                secondColumn={<>
                
                    <Section.Row label={t("payfac.pageRatesRepassTimeSight")}
                        content={val(formatDelay(seller?.sightCreditCardConfigData?.delayForRedeem))} />
                        <Section.Row label={t("payfac.pageRatesRepassTimeInstallments")} content={val(formatDelay(seller?.earlyCreditCardConfigData?.delayForRedeem))} />
                        </>
                    }
            >

                <Section.Row
                    label={t("payfac.pageRatesReceiptTypeLabel")}
                    content={val(receiptType === "EARLY" ? t("payfac.pageRatesReceiptTypeEarly") : t("payfac.pageRatesReceiptTypeTimeline"))}
                />
                <Section.Row label={t("payfac.pageRatesTableColumnSellerRate")}
                    content={val(formatPercentage(seller?.sightCreditCardConfigData?.rate))} />

                <Table<CreditInstallmentRow>
                    columns={creditColumns}
                    data={buildCreditRows()}
                    rowKey={(record) => record.key}
                    
                />
            </Section>

            <ModalImportRates
                isOpen={openModal === "import"}
                onClose={() => setOpenModal("")}
                isLoading={isPending}
                onHandleSubmit={handleImportSubmit}
            />



            <ModalEditFlatRate
                isOpen={openModal === "pix"}
                onClose={() => setOpenModal("")}
                title={t("payfac.pageRatePlanModalEditPixTitle")}
                icon={<PixLogoIcon />}
                data={buildFlatRateData("pixConfigData")}
                buyerRate={buyer?.pixConfigData?.rate ?? 0}
                buyerDelay={buyer?.pixConfigData?.delayForRedeem ?? 0}
                showRateLimit
                availableDelays={availableDelays}
                isLoading={isPending}
                onHandleSubmit={(formData) => handleFlatSubmit("pixConfigData", formData)}
            />

            <ModalEditFlatRate
                isOpen={openModal === "bankSlip"}
                onClose={() => setOpenModal("")}
                title={t("payfac.pageRatePlanModalEditBoletoTitle")}
                icon={<BarcodeIcon />}
                data={buildFlatRateData("bankSlipConfigData")}
                buyerRate={0}
                buyerDelay={0}
                showRateLimit
                showRateType
                availableDelays={availableDelays}
                isLoading={isPending}
                onHandleSubmit={(formData) => handleFlatSubmit("bankSlipConfigData", formData)}
            />

            <ModalEditFlatRate
                isOpen={openModal === "debit"}
                onClose={() => setOpenModal("")}
                title={t("payfac.pageRatePlanModalEditDebitTitle")}
                icon={<CreditCardIcon />}
                data={buildFlatRateData("debitCardConfigData")}
                buyerRate={0}
                buyerDelay={0}
                availableDelays={availableDelays}
                isLoading={isPending}
                onHandleSubmit={(formData) => handleFlatSubmit("debitCardConfigData", formData)}
            />

            <ModalEditCreditSimple
                isOpen={openModal === "credit"}
                onClose={() => setOpenModal("")}
                creditType="online"
                sellerRates={seller?.earlyCreditCardConfigData?.sellerRates || []}
                buyerRates={seller?.earlyCreditCardConfigData?.buyerRates || []}
                delayForRedeem={seller?.earlyCreditCardConfigData?.delayForRedeem ?? null}
                sightCreditDelay={seller?.sightCreditCardConfigData?.delayForRedeem ?? null}
                sightCreditRate={seller?.sightCreditCardConfigData?.rate ?? null}
                receiptType={receiptType}
                availableDelays={availableDelays}
                isLoading={isPending}
                onHandleSubmit={handleCreditSubmit}
            />
        </div>
    );
};
